import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import fr from 'facet-rest';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import { getBorder, getBorderRadius } from 'Components/utils';
import NotActivePicker
  from 'Views/Page/Content/Templates/WhatIfProposalPreviewTemplate/MainContentPicker/MainContentDefault/RightSide/ScenariosPicker/Scenarios/Scenario/NotActivePicker';
import WhatIfClientScenarioDetails from 'Components/ComponentLibrary/Organisms/WhatIfClientScenarioDetails';
import VerticalStack from 'Components/ComponentLibrary/Atoms/Layout/VerticalStack';
import StackItem from 'Components/ComponentLibrary/Atoms/Layout/StackItem';
import { hasRelationship } from 'Helpers/Affordances';
import RoundedButton from 'Components/ComponentLibrary/Components/Uncategorized/RoundedButton';
import { useRightSidebarContext } from 'Views/Page/Content/RightSidebar/RightSidebarContext';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';

const styles = (theme) => ({
  root: {
    position: 'relative',
    ...getBorder(theme, {
      all: true, width: 2, borderStyle: 'dashed', color: theme.palette.grey[200],
    }),
    ...getBorderRadius(theme.radius.md, { all: true }),
  },
  scenarioContainer: {
    height: 800,
    overflowX: 'hidden',
    overflowY: 'scroll',
    overscrollBehavior: 'contain',
    width: 400,
    ...getBorderRadius(theme.radius.md, { all: true }),
  },
  text: {
    paddingBottom: theme.spacing(2),
  },
  selectedContainer: {
    backgroundColor: theme.palette.facet.accentBlue20,
  },
  reviewInsightContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
});

const Scenario = ({
  className,
  classes,
  scenarioResource,
}) => {
  const { pageContentCollection, loadPageContentLink, pageSelfLinkParam } = usePageContentContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const { clearRightSidebarCollection } = useRightSidebarContext();
  const [isLoading, setIsLoading] = useState(false);
  const [facetPlanClientViewResource] = scenarioResource.attr('scenario');
  const isSelected = scenarioResource.attr('isSelected') === 'true';
  const isPassingValidation = scenarioResource.attr('isPassingVerification') === 'true';

  const handleOnClick = async () => {
    if (!hasRelationship(scenarioResource, fr.relationships.select.name)) {
      return;
    }
    // don't attempt to switch scenario that isn't passing validation
    if (!isPassingValidation) {
      return;
    }
    // don't re-fetch if it is the same link
    if (pageSelfLinkParam === scenarioResource.relationship(fr.relationships.select.name).link('related').url()) {
      return;
    }
    logStandardizedEvent(categories.whatIf, eventTypes.clickButton, 'Proposal Preview - Scenario clicked - Review CFP Insights');
    setIsLoading(true);
    // clear right sidebar if loading new insights
    clearRightSidebarCollection();
    await loadPageContentLink(scenarioResource.relationship(fr.relationships.select.name).link('related'));
    setIsLoading(false);
  };

  return (
    <Box
      className={classNames(classes.root, isSelected ? classes.selectedContainer : {}, className)}
      onClick={async () => {
        await handleOnClick();
      }}
    >
      <VerticalStack>
        <StackItem paddingTop={2}>
          <Text
            className={classes.text}
            text={scenarioResource.attr('label')}
            variant={'h5'}
            weight={'bold'}
          />
        </StackItem>
        <StackItem fillsSpace padding={2}>
          <Paper
            className={classes.scenarioContainer}
            variant={'tight'}
          >
            <NotActivePicker
              resource={scenarioResource}
            />
            <WhatIfClientScenarioDetails
              rounded
              facetPlanClientViewCollection={pageContentCollection}
              facetPlanClientViewResource={facetPlanClientViewResource}
              isProposalPreview
            />
          </Paper>
        </StackItem>
        <StackItem paddingBottom={2} fillsSpace>
          { !isSelected ? (
            <Box className={classes.reviewInsightContainer}>
              <RoundedButton
                color="primary"
                variant="contained"
                padding="squished"
                disableElevation
                isLoading={isLoading}
                disabled={isSelected}
              >
                {'Review CFP Insights'}
              </RoundedButton>
            </Box>
          ) : null}
        </StackItem>
      </VerticalStack>
    </Box>
  );
};

Scenario.defaultProps = {
  className: '',
};

Scenario.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  scenarioResource: PropTypes.object.isRequired,
};

export default withStyles(styles)(Scenario);
